import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { mediaUrl, extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Clock, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useStoreBase } from "@/lib/store";
import { useCustomerAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { OndemandTracking } from "./OndemandLiveTrack";
import { trackStoreEvent } from "@/lib/storeAds";

const CANCELLABLE = ["placed", "confirmed", "whatsapp_pending"];

function OrderCancelBox({ config, order, slug, onDone }) {
  const { user } = useCustomerAuth();
  const { base } = useStoreBase();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!config?.store_info?.order_cancellation || !order) return null;
  const req = order.cancel_request || {};
  if (order.status === "cancelled" || req.status === "approved") {
    return <div className="mt-6 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-700" data-testid="order-cancel-done">This order was cancelled.</div>;
  }
  if (req.status === "pending") {
    return (
      <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" data-testid="order-cancel-pending">
        Cancellation requested. The store will approve or decline it.
        {req.reason ? <div className="mt-1">Reason: {req.reason}</div> : null}
      </div>
    );
  }
  if (!CANCELLABLE.includes(order.status)) return null;
  if (order.customer_id && !user) {
    return (
      <div className="mt-6 text-sm text-stone-600" data-testid="order-cancel-signin">
        <Link to={`${base}/account/login`} className="font-semibold underline">Sign in</Link> to cancel this order.
      </div>
    );
  }
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/store/${slug}/orders/${order.id}/cancel`, { reason });
      toast.success("Cancellation request sent");
      setReason("");
      onDone?.();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 rounded-xl border border-stone-200 p-4" data-testid="order-cancel-box">
      <div className="font-semibold text-stone-900">Cancel this order</div>
      <p className="text-xs text-stone-500 mt-1">Tell the store why. The order stays active until they approve the cancellation.</p>
      {req.status === "rejected" ? <p className="text-xs text-red-600 mt-2">The store declined the last request. You can send another reason.</p> : null}
      <Textarea data-testid="order-cancel-reason" className="mt-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for cancellation" />
      <Button type="button" variant="outline" className="mt-3" disabled={busy || reason.trim().length < 3} data-testid="order-cancel-submit" onClick={send}>
        {busy ? "Sending…" : "Request cancellation"}
      </Button>
    </div>
  );
}

export default function StorefrontOrder({ config }) {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const { slug, base } = useStoreBase();
  const [params] = useSearchParams();
  const gw = params.get("gw");
  const [o, setO] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const fetchOrder = useCallback(async () => {
    const { data } = await api.get(`/store/${slug}/orders/${orderId}`);
    setO(data);
    return data;
  }, [slug, orderId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const order = await fetchOrder();
      // For redirect gateways, confirm payment server-side after the shopper returns.
      const needsVerify = order && !["paid", "partial_paid", "cod_pending"].includes(order.payment_status) &&
        ["cashfree", "instamojo", "phonepe", "razorpay", "stripe", "paypal"].includes(order.collect_via || order.payment_method);
      if (needsVerify) {
        setVerifying(true);
        for (let i = 0; i < 3 && !cancelled; i++) {
          try {
            const { data } = await api.post(`/store/${slug}/payment/verify`, { order_id: orderId });
            if (data.paid) break;
          } catch (e) { /* keep retrying briefly */ }
          await new Promise((r) => setTimeout(r, 1800));
        }
        if (!cancelled) { await fetchOrder(); setVerifying(false); }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [fetchOrder, slug, orderId, gw]);

  useEffect(() => {
    if (!o || verifying) return;
    const isOnline = ["razorpay", "cashfree", "instamojo", "payu", "phonepe", "stripe", "paypal"].includes(o.collect_via || o.payment_method);
    const pendingOnline = isOnline && !["paid", "partial_paid", "cod_pending"].includes(o.payment_status);
    if (pendingOnline) return;
    const key = `ms-ads-purchase:${o.id || orderId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* private mode still fires once this mount */ }
    trackStoreEvent("purchase", {
      value: Number(o.total || 0),
      currency: o.currency || "INR",
      transaction_id: o.order_number || orderId,
      content_ids: (o.items || []).map((i) => i.product_id || i.id).filter(Boolean),
    });
  }, [o, verifying, orderId]);

  if (!o) return <div className="p-16 text-center text-stone-500">Loading…</div>;

  const paid = o.payment_status === "paid";
  const partialPaid = o.payment_status === "partial_paid";
  const isWhatsapp = o.payment_method === "whatsapp";
  const isOnline = ["razorpay", "cashfree", "instamojo", "payu", "phonepe", "stripe", "paypal"].includes(o.collect_via || o.payment_method);
  const pendingOnline = isOnline && !paid && !partialPaid && o.payment_status !== "cod_pending";
  const waPending = isWhatsapp && o.status === "whatsapp_pending";
  const pp = o.partial_payment;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center">
        {verifying ? (
          <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center"><Loader2 className="w-7 h-7 text-blue-600 animate-spin" /></div>
        ) : pendingOnline ? (
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center"><Clock className="w-7 h-7 text-amber-600" /></div>
        ) : (
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center"><CheckCircle2 className="w-7 h-7 text-emerald-600" /></div>
        )}
        <h1 className="mt-4 font-display text-3xl font-bold text-stone-900">
          {verifying ? "Confirming your payment…" : pendingOnline ? "Payment pending" : waPending ? "WhatsApp order sent" : partialPaid ? "Advance received" : "Thank you!"}
        </h1>
        <p className="text-stone-500 text-sm mt-1">
          Your order <span className="font-mono-nums font-semibold text-stone-800">{o.order_number}</span>{" "}
          {verifying ? "is being confirmed." : pendingOnline ? "was saved but payment isn't confirmed yet." : waPending ? "was sent on WhatsApp. The store will confirm it shortly." : partialPaid ? "has an advance paid. Pay the remaining balance on delivery." : "has been placed."}
        </p>
        {!verifying && (
          <span className={`inline-flex items-center gap-1.5 mt-3 text-xs font-semibold px-2.5 py-1 rounded-full ${paid || partialPaid ? "bg-emerald-100 text-emerald-700" : o.payment_method === "cod" ? "bg-stone-100 text-stone-600" : isWhatsapp ? "bg-emerald-50 text-emerald-800" : "bg-amber-100 text-amber-700"}`} data-testid="order-payment-status">
            {paid ? "Paid" : partialPaid ? "Advance paid · COD balance due" : o.payment_method === "cod" ? "Cash on Delivery" : isWhatsapp ? "WhatsApp order · awaiting confirmation" : "Awaiting payment"}
          </span>
        )}
      </div>
      <Card className="mt-8 p-6 border-stone-200/70">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">Items</div>
        <div className="divide-y divide-stone-100">
          {o.items.map((it) => (
            <div key={it.product_id} className="py-3 flex items-center justify-between text-sm gap-3">
              <div>
                <div className="font-medium text-stone-900">{it.name}</div>
                <div className="text-xs text-stone-500 font-mono-nums">× {it.qty}{it.is_digital ? " · Digital" : ""}</div>
                {it.is_abp && it.abp ? (
                  <ul className="mt-1 text-xs text-stone-500" data-testid="order-abp">
                    <li>{it.abp.target_kg} KG{it.abp.mix_name ? ` · ${it.abp.mix_name}` : ""}</li>
                    {(it.abp.items || []).map((b) => <li key={b.product_id}>{b.name} × {b.qty_kg} KG</li>)}
                    {(it.abp.services || []).map((s, i) => <li key={i}>{s.name}: ₹{s.amount}</li>)}
                  </ul>
                ) : null}
                {it.is_box && (it.box_items || []).length > 0 && (
                  <ul className="mt-1 text-xs text-stone-500" data-testid="order-box-items">
                    {it.box_items.map((b) => <li key={b.product_id}>{b.name} × {b.qty}</li>)}
                  </ul>
                )}
                {it.is_digital && (it.digital_file_url || it.digital_link) && (
                  <a href={mediaUrl(it.digital_file_url) || it.digital_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-violet-700 mt-1">
                    <Download className="w-3 h-3" /> {it.digital_file_url ? "Download file" : "Open file link"}
                  </a>
                )}
              </div>
              <div className="font-mono-nums font-semibold">₹{it.line_total.toLocaleString("en-IN")}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-stone-100 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span className="font-mono-nums">₹{o.subtotal.toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Shipping</span><span className="font-mono-nums">₹{o.shipping.toLocaleString("en-IN")}</span></div>
          {(o.charges || []).map((c) => (
            <div key={c.id || c.name} className="flex justify-between" data-testid="order-charge">
              <span className="text-stone-500">{c.name}{c.kind === "percent" ? ` (${c.value}%)` : ""}</span>
              <span className="font-mono-nums">₹{Number(c.amount || 0).toLocaleString("en-IN")}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold pt-1"><span>Total</span><span className="font-mono-nums">₹{o.total.toLocaleString("en-IN")}</span></div>
          {pp && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 space-y-1 text-sm" data-testid="order-partial-payment">
              <div className="flex justify-between text-emerald-800"><span>Advance paid</span><span className="font-mono-nums">₹{Number(pp.advance_paid || pp.advance_amount || 0).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-emerald-800"><span>COD balance</span><span className="font-mono-nums">₹{Number(pp.cod_balance || 0).toLocaleString("en-IN")}</span></div>
            </div>
          )}
        </div>
      </Card>
      {!verifying && !pendingOnline ? <OrderCancelBox config={config} order={o} slug={slug} onDone={fetchOrder} /> : null}
      <OndemandTracking slug={slug} orderId={orderId} config={config} order={o} />
      <div className="mt-6 text-center">
        <Link to={`${base}/`}><Button className="btn-store-primary rounded-full" data-testid="order-continue-shopping">{t("store.continueShopping")}</Button></Link>
      </div>
    </div>
  );
}
